import api from '.';
import {
  ProviderType,
  ImageType,
  MinimumVisitTime,
  ShortlistItemProvider,
} from '@karehero/models';

// API response types

interface APIProvider {
  id?: string | null;
  regulatoryId: string;
  name: string;
  careType: string;
  address: {
    streetAddress1?: string | null;
    streetAddress2?: string | null;
    city?: string;
    district?: string;
    county?: string;
    region?: string;
    constituentCountry?: string;
    country?: string;
    postcode?: string;
  };
  location: { lat: number; lon: number };
  distanceMiles?: number | null;
  rating: {
    overallRating?: string;
    overallRatingIndex?: number;
    ratings?: {
      caring?: string;
      effective?: string;
      responsive?: string;
      safe?: string;
      wellLed?: string;
    };
    inspectionDate?: string | null;
  };
  careServiceTypes?: { id?: number; name?: string }[];
  regulatoryStatus?: string;
  isPremium?: boolean | null;
  availability?: boolean | null;
  pricePerHour?: number | null;
  pricePerWeek?: number | null;
  minimumVisitTime?: number | null;
  bedCount?: number | null;
  enquiryPhone?: string;
  enquiryEmail?: string;
  description?: string | null;
  photos?: ImageType[] | null;
  specialisms?: string[] | null;
  url?: string;
}

interface APIGeoPoint {
  lat: number;
  lon: number;
}

interface APISearchResult {
  providers: APIProvider[];
  pagination: {
    page: number;
    perPage: number;
    totalItems: number;
    totalPages: number;
  };
  searchLocation?: APIGeoPoint | null;
}

// Transform API response → ProviderType (same shape llama UI expects)

const minimumVisitMap: Record<number, string> = {
  15: '15 minutes',
  30: '30 minutes',
  45: '45 minutes',
  60: '1 hour',
  120: '2 hours or more',
  1440: 'Live-in care',
};

const transformProvider = (p: APIProvider): ProviderType => ({
  id: p.id ?? undefined,
  regulatoryId: p.regulatoryId,
  regulator: 'cqc',
  name: p.name,
  careType: p.careType === 'care_home' ? 'careHome' : 'homeCare',
  regulatoryStatus: p.regulatoryStatus,
  availability: p.availability ?? undefined,
  pricePerWeek: p.pricePerWeek ?? undefined,
  enquiryPhone: p.enquiryPhone,
  enquiryEmail: p.enquiryEmail,
  description: p.description ?? undefined,
  bedCount: p.bedCount ?? undefined,
  address: {
    streetAddress1: p.address.streetAddress1 ?? undefined,
    streetAddress2: p.address.streetAddress2 ?? undefined,
    city: p.address.city,
    district: p.address.district,
    county: p.address.county,
    region: p.address.region,
    constituentCountry: p.address.constituentCountry,
    country: p.address.country,
    postcode: p.address.postcode,
  },
  coverPhoto: p.photos?.[0] as ImageType | undefined,
  photos: (p.photos as ImageType[]) || [],
  ratings: (p.rating.ratings
    ? {
        overallRating: p.rating.overallRating,
        caring: p.rating.ratings.caring,
        effective: p.rating.ratings.effective,
        responsive: p.rating.ratings.responsive,
        safe: p.rating.ratings.safe,
        wellLed: p.rating.ratings.wellLed,
      }
    : { overallRating: p.rating.overallRating }) as ProviderType['ratings'],
  specialisms: p.specialisms ?? undefined,
  location: p.location
    ? { lat: p.location.lat, lng: p.location.lon }
    : undefined,
  careServiceTypes: p.careServiceTypes?.map((st) => st.name ?? ''),
  minimumVisitTime: p.minimumVisitTime
    ? ({
        time: p.minimumVisitTime,
        label: minimumVisitMap[p.minimumVisitTime],
      } as MinimumVisitTime)
    : undefined,
  hourlyRate: p.pricePerHour ?? undefined,
});

// Query param types

interface CareHomeSearchParams {
  location?: string;
  lat?: number;
  lon?: number;
  distance?: string;
  location_type?: string;
  location_slug?: string;
  ratings?: string[];
  min_rating_index?: number;
  service_types?: string[];
  dementia?: boolean;
  fundings?: string[];
  sort?: string;
  page?: number;
  per_page?: number;
}

interface HomeCareSearchParams {
  location?: string;
  lat?: number;
  lon?: number;
  distance?: string;
  location_type?: string;
  location_slug?: string;
  ratings?: string[];
  min_rating_index?: number;
  hourly_rates?: string[];
  max_hourly_rate?: number;
  visit_duration?: string;
  sort?: string;
  page?: number;
  per_page?: number;
}

const buildQueryString = (params: Record<string, unknown>): string => {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      value.forEach((v) => parts.push(`${key}=${encodeURIComponent(v)}`));
    } else {
      parts.push(`${key}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.join('&');
};

// Pages of one search share a cache entry; each new page is added to the end.

interface PagedArgs {
  page?: number;
}

interface PagedResult {
  hasMoreResults: boolean;
  items: ShortlistItemProvider[];
  pagination: APISearchResult['pagination'];
}

export const pagedSearch = {
  serializeQueryArgs: ({
    endpointName,
    queryArgs,
  }: {
    endpointName: string;
    queryArgs: PagedArgs;
  }) => {
    const { page, ...search } = queryArgs;
    return `${endpointName}:${JSON.stringify(search)}`;
  },
  merge: (
    current: PagedResult,
    incoming: PagedResult,
    { arg }: { arg: PagedArgs },
  ) => {
    if (arg.page && arg.page > 1) {
      incoming.items = [...current.items, ...incoming.items];
    }
    return incoming;
  },
  forceRefetch: ({
    currentArg,
    previousArg,
  }: {
    currentArg?: PagedArgs;
    previousArg?: PagedArgs;
  }) => currentArg?.page !== previousArg?.page,
};

// Endpoints

export const findCareApi = api.injectEndpoints({
  endpoints: (builder) => ({
    searchCareHomes: builder.query<
      {
        hasMoreResults: boolean;
        items: ProviderType[];
        pagination: APISearchResult['pagination'];
        searchLocation?: APIGeoPoint | null;
      },
      CareHomeSearchParams
    >({
      query: (params) =>
        `find-care/care-homes?${buildQueryString(params as unknown as Record<string, unknown>)}`,
      transformResponse: (response: APISearchResult) => ({
        hasMoreResults:
          response.pagination.page < response.pagination.totalPages,
        items: response.providers.map(transformProvider),
        pagination: response.pagination,
        searchLocation: response.searchLocation,
      }),
    }),
    searchHomeCare: builder.query<
      {
        hasMoreResults: boolean;
        items: ProviderType[];
        pagination: APISearchResult['pagination'];
        searchLocation?: APIGeoPoint | null;
      },
      HomeCareSearchParams
    >({
      query: (params) =>
        `find-care/home-care?${buildQueryString(params as unknown as Record<string, unknown>)}`,
      transformResponse: (response: APISearchResult) => ({
        hasMoreResults:
          response.pagination.page < response.pagination.totalPages,
        items: response.providers.map(transformProvider),
        pagination: response.pagination,
        searchLocation: response.searchLocation,
      }),
    }),
    getProviderByRegulatoryId: builder.query<ProviderType | null, string>({
      query: (regulatoryId) => `find-care/providers/${regulatoryId}`,
      transformResponse: (response: APIProvider) => {
        if (!response) return null;
        return transformProvider(response);
      },
    }),
    adminSearchCareHomes: builder.query<
      {
        hasMoreResults: boolean;
        items: ShortlistItemProvider[];
        pagination: APISearchResult['pagination'];
        searchLocation?: APIGeoPoint | null;
      },
      CareHomeSearchParams
    >({
      ...pagedSearch,
      query: (params) =>
        `find-care/care-homes?${buildQueryString(params as unknown as Record<string, unknown>)}`,
      transformResponse: (response: APISearchResult) => ({
        hasMoreResults:
          response.pagination.page < response.pagination.totalPages,
        items: response.providers as unknown as ShortlistItemProvider[],
        pagination: response.pagination,
        searchLocation: response.searchLocation,
      }),
    }),
    adminSearchHomeCare: builder.query<
      {
        hasMoreResults: boolean;
        items: ShortlistItemProvider[];
        pagination: APISearchResult['pagination'];
      },
      HomeCareSearchParams
    >({
      ...pagedSearch,
      query: (params) =>
        `find-care/home-care?${buildQueryString(params as unknown as Record<string, unknown>)}`,
      transformResponse: (response: APISearchResult) => ({
        hasMoreResults:
          response.pagination.page < response.pagination.totalPages,
        items: response.providers as unknown as ShortlistItemProvider[],
        pagination: response.pagination,
      }),
    }),
  }),
});

export const {
  useSearchCareHomesQuery,
  useSearchHomeCareQuery,
  useGetProviderByRegulatoryIdQuery,
  useAdminSearchCareHomesQuery,
  useAdminSearchHomeCareQuery,
} = findCareApi;

// Helper to convert DirectoryInteractions context request → API params

const milesToKm = (miles: number) => `${Math.round(miles * 1.60934)}km`;

const enabledKeys = (dict: Record<string, boolean> | undefined): string[] =>
  dict ? Object.keys(dict).filter((k) => dict[k]) : [];

interface DirectoryRequest {
  searchTerm?: string;
  filters?: {
    careType?: { selectedValue?: string };
    distance?: { selectedValue?: number };
    regulatorRatings?: { selectedValues?: Record<string, boolean> };
    careProvided?: { selectedValues?: string[] };
    hourlyRates?: { selectedValues?: Record<string, boolean> };
    careVisitDuration?: { selectedValue?: string };
  };
  page?: number;
}

export const buildCareHomeParams = (
  req: DirectoryRequest,
): CareHomeSearchParams => ({
  location: req.searchTerm,
  distance: req.filters?.distance?.selectedValue
    ? milesToKm(req.filters.distance.selectedValue)
    : undefined,
  ratings: enabledKeys(req.filters?.regulatorRatings?.selectedValues),
  service_types: req.filters?.careProvided?.selectedValues?.length
    ? req.filters.careProvided.selectedValues
    : undefined,
  page: req.page || 1,
  per_page: 10,
});

export const buildHomeCareParams = (
  req: DirectoryRequest,
): HomeCareSearchParams => ({
  location: req.searchTerm,
  distance: req.filters?.distance?.selectedValue
    ? milesToKm(req.filters.distance.selectedValue)
    : undefined,
  ratings: enabledKeys(req.filters?.regulatorRatings?.selectedValues),
  hourly_rates: enabledKeys(req.filters?.hourlyRates?.selectedValues),
  visit_duration:
    req.filters?.careVisitDuration?.selectedValue !== 'on'
      ? req.filters?.careVisitDuration?.selectedValue
      : undefined,
  page: req.page || 1,
  per_page: 10,
});
